import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDnbCarouselLogic } from './useDnbCarouselLogic';
import { DNB_CAROUSEL_SURFACE } from './dnbCarousel.consts';

const SCROLL_SETTLE_MS = 120;

// Distance between two consecutive cards, measured from the DOM so the maths stays in sync with the
// styles. The cards sit in a single flex row, so one factor covers the whole track.
const getStep = (scroller: HTMLDivElement) => {
  const [first, second] = [...scroller.children] as HTMLElement[];
  return first && second ? second.offsetLeft - first.offsetLeft : 0;
};

// Real slide `i` sits one position along the track because of the leading copy.
const slidePosition = (index: number) => index + 1;

export const DnbCarouselMobile = () => {
  const { t } = useTranslation();
  const { slides, activeIndex, goToSlide } = useDnbCarouselLogic();
  const slideCount = slides.length;
  // The carousel only starts looping once it has been swiped (by hand or by autoplay). The first
  // slide has no neighbour to its left before that; afterwards the track keeps a copy of the last
  // slide in front of it, so even the first slide shows the last one peeking on the left and a
  // swipe forward off the last slide has somewhere to land that looks like the first slide. Two
  // copies trail the last slide so that landing position keeps the same right-hand neighbour as the
  // real first slide, which makes the snap back to it invisible.
  const [hasLooped, setHasLooped] = useState(false);
  const loopSlides = [
    {
      key: `leading-${slides[slideCount - 1].kind}`,
      slide: slides[slideCount - 1],
      isClone: true,
      isLeading: true,
    },
    ...slides.map((slide) => ({
      key: `real-${slide.kind}`,
      slide,
      isClone: false,
      isLeading: false,
    })),
    { key: `trailing-${slides[0].kind}`, slide: slides[0], isClone: true, isLeading: false },
    { key: `trailing-${slides[1].kind}`, slide: slides[1], isClone: true, isLeading: false },
  ];

  const scrollerRef = useRef<HTMLDivElement>(null);
  const previousIndexRef = useRef<number | null>(null);
  // Set when a swipe already settled on the copy, so the sync effect does not ride it a second time.
  const positionedBySwipeRef = useRef(false);
  const isFirstScrollRef = useRef(true);

  const scrollToSlide = useCallback((position: number, smooth: boolean) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.style.scrollBehavior = smooth ? 'smooth' : 'auto';
    scroller.scrollLeft = position * getStep(scroller);
  }, []);

  // Leaving the first slide switches the carousel into its looping mode. User-driven navigation
  // calls this alongside `goToSlide` so the latch batches into the same render.
  const unlockLoop = useCallback(() => setHasLooped(true), []);

  // Auto-play and the dots drive `activeIndex`; keep the scroller on the matching card. This runs
  // before paint so the leading copy is never shown as the starting slide.
  useLayoutEffect(() => {
    const previous = previousIndexRef.current;
    previousIndexRef.current = activeIndex;
    // Wrapping forward off the last slide rides the copy of the first slide, so the loop continues
    // in the same direction instead of rewinding the whole track.
    const wrapped =
      !positionedBySwipeRef.current && previous === slideCount - 1 && activeIndex === 0;
    positionedBySwipeRef.current = false;
    scrollToSlide(wrapped ? slideCount + 1 : slidePosition(activeIndex), !isFirstScrollRef.current);
    isFirstScrollRef.current = false;
    // Autoplay advances `activeIndex` on its own, so the latch is handled here for that path.
    if (activeIndex !== 0) unlockLoop();
  }, [activeIndex, scrollToSlide, slideCount, unlockLoop]);

  // Once a swipe settles, map the scroll offset back to a slide, looping off the copies.
  const handleScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const step = getStep(scroller);
    if (!step) return;
    // Clamp the low end: Safari reports a negative `scrollLeft` while rubber-banding before the
    // first slide, and the carousel must not wrap backwards into the leading copy.
    const position = Math.max(0, Math.round(scroller.scrollLeft / step));
    if (position < slidePosition(0)) {
      scrollToSlide(slidePosition(0), false);
      if (activeIndex !== 0) goToSlide(0);
      return;
    }
    if (position > slideCount) {
      // The swipe came to rest on a trailing copy, which shows the same thing as its real slide.
      // Flag it as already positioned, or the effect below would ride the copy again and replay the
      // track.
      const realIndex = (position - slidePosition(0)) % slideCount;
      scrollToSlide(slidePosition(realIndex), false);
      if (activeIndex !== realIndex) {
        positionedBySwipeRef.current = true;
        if (realIndex !== 0) unlockLoop();
        goToSlide(realIndex);
      }
      return;
    }
    const realIndex = position - 1;
    if (realIndex !== 0) unlockLoop();
    goToSlide(realIndex);
  }, [activeIndex, goToSlide, scrollToSlide, slideCount, unlockLoop]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;
    let timeout: number;
    const onScroll = () => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(handleScroll, SCROLL_SETTLE_MS);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.clearTimeout(timeout);
      scroller.removeEventListener('scroll', onScroll);
    };
  }, [handleScroll]);

  return (
    <div className='flex flex-col items-center w-full'>
      {/* A gutter of half the viewport minus half a card, so the active card sits centred with a
          neighbour peeking either side. Centring needs track either side of the card, so this is
          geometry rather than an inset. It also cancels out of the maths: slide `n` always settles
          at `n * card + gap`, whatever the viewport. */}
      <div
        ref={scrollerRef}
        data-testid='dnb-mobile-carousel-track'
        className='relative flex gap-[8px] w-full overflow-x-auto overscroll-x-contain snap-x snap-mandatory no-scrollbar pl-[calc(50%-164.5px)]'>
        {loopSlides.map(({ key, slide, isClone, isLeading }) => (
          <div
            key={key}
            data-testid={isClone ? 'dnb-mobile-carousel-clone' : 'dnb-mobile-carousel-card'}
            aria-hidden={isClone || undefined}
            className={`flex flex-col items-center justify-end shrink-0 w-[329px] h-[333px] rounded-[12px] overflow-hidden snap-always ${
              isLeading && !hasLooped ? 'invisible snap-none' : 'snap-center'
            }`}
            style={{ background: DNB_CAROUSEL_SURFACE }}>
            <div className='flex flex-1 items-center justify-center w-full min-h-0 p-[16px]'>
              <img
                src={slide.compactImage}
                alt={t(`pages.dnbEmbeddedIntro.carousel.${slide.kind}Alt`)}
                width={slide.mobileWidth}
                height={slide.mobileHeight}
                draggable={false}
                className='max-w-full max-h-full object-contain drop-shadow-[0_22px_44px_rgba(2,8,35,0.48)]'
              />
            </div>
            <p className='w-full px-[40px] pb-[24px] text-center text-[14px] leading-[20px] text-text-main-primary-inverse'>
              <Trans
                i18nKey={`pages.dnbEmbeddedIntro.carousel.${slide.kind}Title`}
                components={{ sup: <sup /> }}
              />
            </p>
          </div>
        ))}
      </div>
      <div className='flex items-center gap-[8px] mt-[24px]'>
        {slides.map((slide, index) => (
          <button
            key={slide.kind}
            type='button'
            aria-label={t('pages.dnbEmbeddedIntro.carousel.goToSlide', { slideNumber: index + 1 })}
            aria-current={activeIndex === index ? 'true' : undefined}
            onClick={() => {
              if (index !== 0) unlockLoop();
              goToSlide(index);
            }}
            className={`size-[8px] rounded-full ${
              activeIndex === index ? 'bg-text-main-secondary' : 'bg-outline-primary-bw-04'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default DnbCarouselMobile;
